import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { bookieLogos } from './CommunityBetTracker';

/**
 * Add-to-tracker modal for the public Community Tips Feed. Lets a subscriber
 * copy a community tip into their own Results Tracker: pick which price to
 * track (the tipped odds or the bookmaker's current odds when available),
 * enter a stake (with unit shortcuts), and confirm.
 */
function AddTipToTrackerModal({ tip, unitSize = 20, onAddToTracker, isAddingBet, onClose }) {
    const [stake, setStake] = useState('');
    const stakeInputRef = useRef(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const timer = setTimeout(() => stakeInputRef.current?.focus(), 50);
        return () => {
            document.body.style.overflow = '';
            clearTimeout(timer);
        };
    }, []);

    if (!tip) return null;

    const tipperLabel = tip.tipper_discord_name || tip.tipper_email || 'Community tipper';
    // Always track at the bookmaker's current price when the live feed has
    // one — that's what the user would actually get placing the bet now.
    // Falls back to the tipped odds only when no live price is available.
    const hasLivePrice = tip.current_bookmaker_odds != null;
    const odds = hasLivePrice ? tip.current_bookmaker_odds : tip.odds;

    const stakeNum = parseFloat(stake);
    const hasPayout = stake !== '' && !isNaN(stakeNum) && stakeNum > 0 && odds;
    const payout = hasPayout ? (stakeNum * odds).toFixed(2) : null;

    const handleSubmit = async () => {
        if (!onAddToTracker || !hasPayout) return;
        await onAddToTracker({
            date: tip.date,
            sport: (tip.sport || 'afl').toUpperCase(),
            game: tip.game,
            bet: tip.bet,
            odds: Number(odds),
            stake: stakeNum,
            payout: parseFloat((stakeNum * odds).toFixed(2)),
            bookie: tip.bookie,
            result: 'pending',
            pl: 0,
            bonus_bet: false
        });
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-slate-900 border border-slate-700/50 rounded-xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
                {/* Header bar */}
                <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                        <h3 className="text-sm font-semibold text-white truncate tracking-tight">Add to Tracker</h3>
                        <div className="w-px h-4 bg-slate-700/50 flex-shrink-0" />
                        <span className="text-xs font-mono text-slate-400 truncate" title={tipperLabel}>Tipped by {tipperLabel}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-800 transition-all ml-3 flex-shrink-0"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Bet readout */}
                <div className="px-4 py-3 border-b border-slate-700/30">
                    <div className="text-[11px] font-mono text-slate-600 mb-1.5">{tip.game}</div>
                    <div className="flex items-start justify-between gap-3">
                        <div className="text-sm text-white font-medium leading-snug" title={tip.bet}>{tip.bet}</div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {bookieLogos[tip.bookie] ? (
                                <img src={bookieLogos[tip.bookie]} alt={tip.bookie} className="w-6 h-6 rounded object-cover" title={tip.bookie} />
                            ) : (
                                <span className="text-xs text-slate-400">{tip.bookie}</span>
                            )}
                            <span className="text-2xl font-bold text-white font-mono tabular-nums tracking-tight">{Number(odds).toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Price note */}
                <div className="px-4 py-3">
                    {hasLivePrice ? (
                        <div className="flex gap-2">
                            <div className="flex-1 py-2 px-2.5 bg-slate-800/30 rounded border border-slate-700/30 text-center">
                                <div className="text-base font-bold text-slate-400 font-mono tabular-nums">{Number(tip.odds).toFixed(2)}</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mt-0.5">Tipped</div>
                            </div>
                            <div className="flex-1 py-2 px-2.5 bg-emerald-500/5 rounded border border-emerald-500/15 text-center">
                                <div className="text-base font-bold text-emerald-400 font-mono tabular-nums">{Number(tip.current_bookmaker_odds).toFixed(2)}</div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mt-0.5">Current · Tracked</div>
                            </div>
                        </div>
                    ) : (
                        <p className="text-[10px] font-mono text-slate-600">No live price available — tracking at the tipped odds. Verify the price with the bookmaker before placing.</p>
                    )}
                </div>

                {/* Stake input + payout preview */}
                <div className="px-4 py-3 border-t border-slate-700/30">
                    <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest mb-2">Stake</div>
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-mono">$</span>
                            <input
                                ref={stakeInputRef}
                                type="number"
                                step="0.01"
                                min="0"
                                value={stake}
                                onChange={(e) => setStake(e.target.value)}
                                placeholder="0.00"
                                className="w-full pl-6 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm font-mono text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500 transition-colors"
                            />
                        </div>
                        <div className="flex gap-1.5">
                            {[{ label: '1U', mult: 1 }, { label: '0.5U', mult: 0.5 }, { label: '0.2U', mult: 0.2 }].map((unit) => {
                                const unitVal = (unitSize * unit.mult).toFixed(2);
                                return (
                                    <button
                                        key={unit.label}
                                        onClick={() => setStake(unitVal)}
                                        className={`px-3 py-2 rounded text-xs font-mono font-medium transition-all border ${
                                            stake === unitVal
                                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        {unit.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {hasPayout && (
                        <div className="flex items-center justify-between mt-3 py-2.5 px-3 bg-emerald-500/5 border border-emerald-500/15 rounded">
                            <div>
                                <div className="text-[10px] font-mono text-slate-600 uppercase tracking-widest">Payout</div>
                                <div className="text-sm font-mono font-bold text-emerald-400 tabular-nums">${payout}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="px-4 py-3 border-t border-slate-700/30 flex gap-2">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 text-xs font-mono font-medium text-slate-400 hover:text-white rounded border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isAddingBet || !hasPayout}
                        onClick={handleSubmit}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded text-xs font-mono font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                        {isAddingBet ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Adding...
                            </span>
                        ) : 'Add to Tracker'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default AddTipToTrackerModal;
